"use client";

import Slider from "rc-slider";
import { MarkObj } from "rc-slider/lib/Marks";
import useNumber from "@/hooks/number";
import {
  Box,
  Button,
  Select,
  MenuItem,
  Typography,
  FormControl,
} from "@mui/material";
import "rc-slider/assets/index.css";
import useUrlParams from "@/hooks/url";

export default function PriceFilter({ range }: { range: (string | number)[] }) {
  const { currency } = useNumber();
  const { updateURLParams, searchParams } = useUrlParams();
  const MIN = 0;
  const MAX = Number(range[range.length - 1]) + 1;
  const priceRange = [MIN, ...range, MAX].map((v, i) => ({
    label:
      Number(v) === MIN
        ? "Min"
        : Number(v) === MAX
          ? `${Number(range[range.length - 1])}+`
          : currency(v),
    value: Number(v),
    position: Math.round((i / (range.length + 1)) * MAX),
  }));
  const min =
    priceRange.find((v) => v.value.toString() == searchParams.get("min")) ??
    priceRange[0];
  const max =
    priceRange.find((v) => v.value.toString() == searchParams.get("max")) ??
    priceRange[priceRange.length - 1];

  const handleChange = (value: number | number[]) => {
    if (Array.isArray(value)) {
      updateURLParams({
        min: String(
          priceRange.find((v) => v.position === value[0])?.value ?? min.value,
        ),
        max: String(
          priceRange.find((v) => v.position === value[1])?.value ?? max.value,
        ),
      });
    }
  };

  const clearFilter = () => {
    updateURLParams({ min: "", max: "" });
  };

  return (
    <Box p={2}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
      >
        <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
          PRICE
        </Typography>
        {(searchParams.get("min") || searchParams.get("max")) && (
          <Button size="small" variant="text" onClick={clearFilter}>
            CLEAR
          </Button>
        )}
      </Box>

      <Slider
        step={null}
        range
        min={MIN}
        max={MAX}
        marks={(() => {
          const marks: { [key: number]: MarkObj } = {};

          priceRange.forEach((v) => {
            marks[v.position] = {
              label: "•",
              style: { fontSize: "16px", top: "-8px", color: "#c2c2c2" },
            };
          });

          return marks;
        })()}
        value={[min.position, max.position]}
        onChange={handleChange}
        styles={{
          handle: {
            borderColor: "#1976d2",
            backgroundColor: "#fff",
            width: "14px",
            height: "14px",
            opacity: 1,
            boxShadow: "0 0 4px rgba(0,0,0,0.2)",
          },
          track: {
            backgroundColor: "#1976d2",
            height: "6px",
          },
          rail: {
            backgroundColor: "#e0e0e0",
            height: "6px",
          },
        }}
        dotStyle={{ display: "none" }}
      />

      <Box mt={3} display="flex" alignItems="center" gap={2}>
        <FormControl fullWidth size="small">
          <Select
            value={min.value}
            onChange={(e) => {
              const val = Number(e.target.value);
              const found = priceRange.find((v) => v.value === val);
              if (found) {
                updateURLParams({ min: String(found.value) });
              }
            }}
          >
            {priceRange.map(
              (price, index) =>
                price.value < max.value &&
                price.value !== MAX && (
                  <MenuItem value={price.value} key={index}>
                    {price.label}
                  </MenuItem>
                ),
            )}
          </Select>
        </FormControl>

        <Typography variant="body2" color="text.secondary">
          to
        </Typography>

        <FormControl fullWidth size="small">
          <Select
            value={max.value}
            onChange={(e) => {
              const val = Number(e.target.value);
              const found = priceRange.find((v) => v.value === val);
              if (found) {
                updateURLParams({ max: String(found.value) });
              }
            }}
          >
            {priceRange.map(
              (price, index) =>
                price.value > min.value &&
                price.value !== MIN && (
                  <MenuItem value={price.value} key={index}>
                    {price.label}
                  </MenuItem>
                ),
            )}
          </Select>
        </FormControl>
      </Box>
    </Box>
  );
}
